import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { getUser } from '@/utils/auth';
import { 
  Home, Building2, Palette, Key, User, Lightbulb, BookOpen, 
  ChevronDown, Briefcase, UserCircle, ListPlus, Menu, X,
  ShoppingCart, Tag, Users, Building, FileText, Landmark, Search,
  Scale, Sparkles, Umbrella, Hotel, Stethoscope, Newspaper,
  LogIn, HardHat, Gift
} from 'lucide-react';

// Nav Dropdown Component (defined outside to avoid re-creation on render)
const NavDropdown = ({ label, Icon, items, testId }) => (
  <div className="relative group">
    <button 
      className="text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 px-2.5 py-2 rounded-lg transition-all duration-200 flex items-center gap-1 whitespace-nowrap"
      data-testid={testId}
    >
      <Icon className="w-4 h-4" />
      {label}
      <ChevronDown className="w-4 h-4 transition-transform duration-200 group-hover:rotate-180" />
    </button>
    
    <div className="absolute top-full left-0 mt-1 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
      <div className="p-2 max-h-96 overflow-y-auto">
        {items.map((item, idx) => (
          <Link
            key={idx}
            to={item.to}
            className="flex items-start gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors group/item"
            data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}-link`}
          >
            <div className={`p-2 rounded-lg transition-colors ${
              item.highlight 
                ? 'bg-amber-100 group-hover/item:bg-amber-200' 
                : 'bg-primary/10 group-hover/item:bg-primary/20'
            }`}>
              <item.icon className={`w-5 h-5 ${
                item.highlight ? 'text-amber-600' : 'text-primary'
              }`} />
            </div>
            <div>
              <p className="font-medium text-gray-900">{item.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </div>
);

// Buy submenu items
const buySubmenu = [
  { 
    to: '/buy/properties', 
    label: 'Browse Properties', 
    icon: Search,
    description: 'Search residential, commercial & agricultural properties'
  },
  { 
    to: '/buy/post-requirement', 
    label: 'Post Requirement', 
    icon: FileText,
    description: 'Let matching sellers find you'
  },
  { 
    to: '/buy/legal-opinion', 
    label: 'Get Legal Opinion', 
    icon: Scale,
    description: 'Verify property legality with experts'
  },
  { 
    to: '/buy/priest-services', 
    label: 'Book a Priest', 
    icon: Sparkles,
    description: 'Griha Pravesh, Vastu Shanti & more',
    highlight: true
  },
];

// Sell submenu items
const sellSubmenu = [
  { 
    to: '/sell/individual', 
    label: 'Individual / Landlord', 
    icon: UserCircle,
    description: 'List your property for free'
  },
  { 
    to: '/sell/agent', 
    label: 'Agent', 
    icon: Users,
    description: 'Packages & features for real estate agents'
  },
  { 
    to: '/sell/developer', 
    label: 'Developer / Enterprise', 
    icon: Building,
    description: 'Large builder portal & bulk listings'
  },
];

// Rent/Lease submenu items
const rentSubmenu = [
  { 
    to: '/rentals/find', 
    label: 'Find a Rental Home', 
    icon: Search,
    description: 'Residential, commercial, studios & more'
  },
  { 
    to: '/rent/individual', 
    label: 'Individual / Landlord', 
    icon: UserCircle,
    description: 'List your rental property — plans & pricing'
  },
  { 
    to: '/rentals/corporate', 
    label: 'Corporate Rentals', 
    icon: Briefcase,
    description: 'Short-term rentals for executives on the move'
  },
  { 
    to: '/rentals/vacation', 
    label: 'Vacation Rentals', 
    icon: Umbrella,
    description: 'Home away from home - fully furnished short stays'
  },
  { 
    to: '/rentals/zen-space', 
    label: 'List as Zen Space', 
    icon: Sparkles,
    description: 'Premium furnished properties with ZenStudios branding'
  },
];

// Interior Design submenu items
const interiorSubmenu = [
  { 
    to: '/interior-design', 
    label: 'Residential', 
    icon: Home,
    description: 'Home interiors with Z-Engine quotes'
  },
  { 
    to: '/interior-design/corporate', 
    label: 'Corporate', 
    icon: Building2,
    description: 'Office spaces & corporate interiors'
  },
  { 
    to: '/interior-design/business', 
    label: 'Businesses', 
    icon: Briefcase,
    description: 'Retail, restaurants & commercial spaces'
  },
  { 
    to: '/interior-design/hospitality', 
    label: 'Hospitality', 
    icon: Hotel,
    description: 'Hotels, resorts & guest houses'
  },
  { 
    to: '/interior-design/healthcare', 
    label: 'Healthcare', 
    icon: Stethoscope,
    description: 'Clinics, hospitals & wellness centers'
  },
];

// Resources/Learn submenu items
const resourcesSubmenu = [
  { 
    to: '/inspiration', 
    label: 'Design Inspiration', 
    icon: Lightbulb,
    description: 'Browse interior design ideas & trends'
  },
  { 
    to: '/blogs', 
    label: 'Insights & Articles', 
    icon: BookOpen,
    description: 'Expert advice on real estate & interiors'
  },
  { 
    to: '/about', 
    label: 'About ZenStudios', 
    icon: Building2,
    description: 'Our story, team & vision'
  },
];

// Login dropdown items - Consumer & Partner facing only
// Note: Employee & Admin logins removed - they have separate apps at employee.zenstudios.in and admin.zenstudios.in
const loginOptions = [
  { 
    to: '/login', 
    label: 'Customer Login', 
    icon: User,
    description: 'Property seekers & homeowners'
  },
  { 
    to: '/agent-login', 
    label: 'Agent Login', 
    icon: Users,
    description: 'Real estate agents & brokers'
  },
  { 
    to: '/developer-login', 
    label: 'Developer Login', 
    icon: HardHat,
    description: 'Builders & property developers'
  },
];

const Navbar = ({ onListPropertyClick }) => {
  const navigate = useNavigate();
  const user = getUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-border shadow-sm" data-testid="main-navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Logo with SVG */}
          <Link to="/" className="flex items-center group flex-shrink-0 gap-2" data-testid="nav-logo-link">
            <img 
              src="/logo-zenstudios.svg" 
              alt="ZenStudios Logo" 
              className="w-9 h-9 transition-transform group-hover:scale-105"
            />
            <div className="text-xl lg:text-2xl transition-transform group-hover:scale-105">
              <span className="text-gray-900 font-bold">Zen</span>
              <span className="text-primary font-bold">Studios</span>
              <sup className="text-xs align-super">™</sup>
            </div>
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden xl:flex items-center gap-0.5 flex-shrink-0">
            {/* Home Link */}
            <Link 
              to="/" 
              className="text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 px-2.5 py-2 rounded-lg transition-all duration-200 flex items-center gap-1 whitespace-nowrap"
              data-testid="nav-home-link"
            >
              <Home className="w-4 h-4" />
              Home
            </Link>

            {/* Rent Dropdown - First after Home */}
            <NavDropdown 
              label="Rent" 
              Icon={Key} 
              items={rentSubmenu} 
              testId="nav-rent-dropdown"
            />

            {/* Buy Dropdown */}
            <NavDropdown 
              label="Buy" 
              Icon={ShoppingCart} 
              items={buySubmenu} 
              testId="nav-buy-dropdown"
            />

            {/* Sell Dropdown */}
            <NavDropdown 
              label="Sell" 
              Icon={Tag} 
              items={sellSubmenu} 
              testId="nav-sell-dropdown"
            />

            {/* Interior Design Dropdown */}
            <NavDropdown 
              label="Interior Design" 
              Icon={Palette} 
              items={interiorSubmenu} 
              testId="nav-interior-dropdown"
            />

            {/* Property Management */}
            <Link 
              to="/property-management" 
              className="text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 px-2.5 py-2 rounded-lg transition-all duration-200 flex items-center gap-1 whitespace-nowrap"
              data-testid="nav-property-management-link"
            >
              <Key className="w-4 h-4" />
              <span className="hidden xl:inline">Management</span>
              <span className="xl:hidden">Mgmt</span>
            </Link>

            {/* Services */}
            <Link 
              to="/services" 
              className="text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 px-2.5 py-2 rounded-lg transition-all duration-200 flex items-center gap-1 whitespace-nowrap"
              data-testid="nav-services-link"
            >
              <Sparkles className="w-4 h-4" />
              Services
            </Link>

            {/* Home Loans - NEW */}
            <Link 
              to="/home-loans" 
              className="text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 px-2.5 py-2 rounded-lg transition-all duration-200 flex items-center gap-1 whitespace-nowrap"
              data-testid="nav-home-loans-link"
            >
              <Landmark className="w-4 h-4" />
              <span className="hidden 2xl:inline">Home Loans</span>
              <span className="2xl:hidden">Loans</span>
            </Link>

            {/* Resources Dropdown - NEW */}
            <NavDropdown 
              label="Learn" 
              Icon={Newspaper} 
              items={resourcesSubmenu} 
              testId="nav-resources-dropdown"
            />
          </div>
          
          {/* Right side buttons */}
          <div className="flex items-center gap-2 sm:gap-4">
            {user ? (
              <>
                <Link to="/referrals">
                  <Button
                    variant="ghost"
                    className="text-sm font-medium hidden sm:flex items-center gap-2 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                    data-testid="nav-refer-earn-btn"
                  >
                    <Gift className="w-4 h-4" />
                    Refer &amp; Earn
                  </Button>
                </Link>
                <Link to="/dashboard">
                  <Button 
                    variant="ghost" 
                    className="text-sm font-medium hidden sm:flex items-center gap-2"
                    data-testid="nav-dashboard-btn"
                  >
                    <User className="w-4 h-4" />
                    Dashboard
                  </Button>
                </Link>
              </>
            ) : (
              <>
                {/* Login Button with Hover Dropdown */}
                <div className="relative group hidden sm:block">
                  <Button 
                    variant="ghost" 
                    className="text-sm font-medium flex items-center gap-1.5"
                    data-testid="nav-login-btn"
                  >
                    <LogIn className="w-4 h-4" />
                    Login
                    <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
                  </Button>
                  
                  {/* Login Options Dropdown */}
                  <div className="absolute top-full right-0 mt-1 w-72 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-3 border-b bg-gray-50">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Select Login Type</p>
                    </div>
                    <div className="p-2">
                      {loginOptions.map((option, idx) => (
                        <Link
                          key={idx}
                          to={option.to}
                          className="flex items-start gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors group/item"
                          data-testid={`login-option-${option.label.toLowerCase().replace(/ /g, '-')}`}
                        >
                          <div className="p-2 rounded-lg bg-primary/10 group-hover/item:bg-primary/20 transition-colors">
                            <option.icon className="w-4 h-4 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 text-sm">{option.label}</p>
                            <p className="text-xs text-gray-500">{option.description}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
                <Link to="/register">
                  <Button className="rounded-full bg-primary px-4 sm:px-6 text-sm" data-testid="nav-register-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
            
            {/* Mobile Menu Button */}
            <button 
              className="xl:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="xl:hidden mt-4 pb-4 border-t pt-4 animate-in slide-in-from-top duration-200 max-h-[80vh] overflow-y-auto">
            <div className="space-y-1">
              {/* Home */}
              <Link 
                to="/" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
              >
                <Home className="w-5 h-5 text-primary" />
                <span className="font-medium">Home</span>
              </Link>
              
              {/* Rent Section - First */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Rent
              </div>
              {rentSubmenu.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <item.icon className="w-5 h-5 text-primary" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {/* Buy Section */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Buy
              </div>
              {buySubmenu.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <item.icon className="w-5 h-5 text-primary" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {/* Sell Section */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Sell
              </div>
              {sellSubmenu.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <item.icon className="w-5 h-5 text-primary" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}
              
              {/* Interior Design Section */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Interior Design
              </div>
              {interiorSubmenu.map((item, idx) => (
                <Link
                  key={idx}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                >
                  <item.icon className="w-5 h-5 text-primary" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              ))}

              {/* Other Links */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Services
              </div>
              <Link 
                to="/services" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                data-testid="mobile-nav-services-link"
              >
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="font-medium">All Services</span>
              </Link>
              <Link 
                to="/property-management" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
              >
                <Key className="w-5 h-5 text-primary" />
                <span className="font-medium">Property Management</span>
              </Link>

              {/* Refer & Earn — logged-in users only */}
              {user && (
                <Link
                  to="/referrals"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-50 hover:bg-amber-100 transition-colors"
                  data-testid="mobile-nav-refer-earn-link"
                >
                  <Gift className="w-5 h-5 text-amber-600" />
                  <div>
                    <span className="font-medium text-amber-700">Refer &amp; Earn</span>
                    <p className="text-xs text-amber-600">Invite friends, earn rewards</p>
                  </div>
                </Link>
              )}
              <Link 
                to="/home-loans" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
              >
                <Landmark className="w-5 h-5 text-primary" />
                <span className="font-medium">Home Loans</span>
              </Link>

              {/* Learn Section */}
              <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Learn
              </div>
              <Link 
                to="/inspiration" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
              >
                <Lightbulb className="w-5 h-5 text-primary" />
                <span className="font-medium">Design Inspiration</span>
              </Link>
              <Link 
                to="/blogs" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
              >
                <BookOpen className="w-5 h-5 text-primary" />
                <span className="font-medium">Insights & Articles</span>
              </Link>
              <Link 
                to="/about" 
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
              >
                <Building2 className="w-5 h-5 text-primary" />
                <span className="font-medium">About ZenStudios</span>
              </Link>

              {/* Login Section for Mobile */}
              {!user && (
                <>
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mt-2 border-t pt-4">
                    Login Options
                  </div>
                  {loginOptions.map((option, idx) => (
                    <Link
                      key={idx}
                      to={option.to}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-primary/5 transition-colors"
                      data-testid={`mobile-login-${option.label.toLowerCase().replace(/ /g, '-')}`}
                    >
                      <option.icon className="w-5 h-5 text-primary" />
                      <div>
                        <span className="font-medium">{option.label}</span>
                        <p className="text-xs text-gray-500">{option.description}</p>
                      </div>
                    </Link>
                  ))}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
